import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import { draftFax, previewFax, sendFax } from 'api/Fax';

import { showAlert } from 'components/shared/Alert/Alert';

import formatFaxData from './formatFaxData';

const useSendFaxFormik = ({
  fax,
  setLoading,
  setSendFaxModalVisible,
  setResetTags,
  setPdfData,
  setPreviewFaxModalVisible,
  clearSelection
}) => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const getCoverPage = async () => {
    const input = document.getElementById('cover-page');
    const canvas = await html2canvas(input, { scale: 1.5, useCORS: true });
    const imgData = canvas.toDataURL('image/jpeg', 0.8);
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
    const pdfBuffer = pdf.output('arraybuffer');

    const file = {
      buffer: pdfBuffer,
      name: 'cover-page' + '.pdf',
      originalname: 'cover-page' + '.pdf',
      mimetype: 'application/pdf',
      size: pdfBuffer.byteLength,
      fieldname: 'files'
    };

    function createFileFromBuffer(buffer, fileName, mimeType) {
      const blob = new Blob([buffer], { type: mimeType });
      const file = new File([blob], fileName, { type: mimeType });
      file['id'] = file.size;
      return file;
    }

    const buffer = new Uint8Array(file.buffer).buffer;
    const createdFile = createFileFromBuffer(buffer, file.name, file.mimetype);
    return createdFile;
  };

  const formik = useFormik({
    initialValues: {
      subject: fax?.subject || '',
      notes: fax?.notes || '',
      deliver_to: fax?.deliver_to || '',
      fax_numbers: fax?.fax_numbers
        ? fax?.fax_numbers.map((number) => ({ fax_number: number }))
        : [{ fax_number: '' }],
      phone_number: '',
      contacts: fax?.fax_contacts || [],
      files: fax?.files || [],
      existing_files: [],
      type: '',
      tag_ids: fax?.tag_ids || []
    },
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      const coverPageFile = await getCoverPage();
      setLoading((prev) => ({ prev, [values.type]: true }));

      switch (values.type) {
        case 'send':
          sendFax(
            navigate,
            formatFaxData({ ...values, files: [coverPageFile, ...values.files] })
          ).then((data) => {
            if (data.code === 0) {
              showAlert({ message: 'Fax sent successfully!', color: 'success' });
              setResetTags(true);
              clearSelection();

              queryClient.refetchQueries(['outboundFaxes']);
              queryClient.invalidateQueries(['practice-documents']);

              resetForm();
              setSendFaxModalVisible(false);
            }
            if (data.code !== 0) {
              if (data.error_message) {
                showAlert({
                  title: data.error_message.name,
                  message: data.error_message.message,
                  color: 'danger'
                });
              } else if (data.error) {
                showAlert({ title: data.error, color: 'danger' });
              } else {
                showAlert({ message: 'Fax failed to send', color: 'danger' });
              }
            }

            setLoading({ draft: false, send: false, preview: false });
          });

          break;
        case 'draft':
          draftFax(navigate, formatFaxData(values)).then((data) => {
            if (data.code === 0) {
              showAlert({ message: 'Fax saved successfully', color: 'success' });
              setResetTags(true);
              clearSelection();

              queryClient.refetchQueries(['draftFaxes']);
              queryClient.invalidateQueries(['practice-documents']);

              resetForm();
            } else {
              showAlert({ message: 'Fax failed to save as draft', color: 'danger' });
            }
            setLoading({ draft: false, send: false, preview: false });
          });
          break;
        case 'preview':
          previewFax(
            navigate,
            formatFaxData({ ...values, files: [coverPageFile, ...values.files] })
          ).then((data) => {
            const uint8Array = new Uint8Array(data?.buffer?.data);
            const blobFile = new Blob([uint8Array], { type: 'application/pdf' });
            const blobUrlFile = URL.createObjectURL(blobFile);
            setPdfData(blobUrlFile);
            setPreviewFaxModalVisible(true);
            setLoading({ draft: false, send: false, preview: false });
          });
          break;
        default:
      }
    }
  });

  return formik;
};

export default useSendFaxFormik;
